import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { BellRing, Loader2, Pause, Play, XCircle, Check } from 'lucide-react';
import api from '@/lib/axios';
import { track } from '@/lib/analytics';

const FREQ_LABEL = { imediato: 'Assim que entrar', diario: 'Resumo diário', semanal: 'Resumo semanal' };

export default function MarketplaceAlertManagePage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  // noindex — página sensível baseada em token
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'robots'; m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);

  const load = useCallback(async () => {
    if (!token) { setErr('Ligação inválida.'); setLoading(false); return; }
    setLoading(true); setErr('');
    try { setData((await api.get(`/marketplace/alerts/manage?token=${encodeURIComponent(token)}`)).data); }
    catch (e) { setErr(e.response?.data?.detail || 'Não foi possível abrir a gestão do alerta.'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => { setBusy(true); setErr(''); try { await fn(); await load(); } catch (e) { setErr(e.response?.data?.detail || 'Ação falhou.'); } finally { setBusy(false); } };
  const changeFreq = (frequencia) => act(() => api.put(`/marketplace/alerts/frequency?token=${encodeURIComponent(token)}&frequencia=${frequencia}`));
  const pause = () => act(() => api.put(`/marketplace/alerts/pause?token=${encodeURIComponent(token)}`));
  const reactivate = () => act(() => api.put(`/marketplace/alerts/reactivate?token=${encodeURIComponent(token)}`));
  const doCancel = async () => {
    setBusy(true); setErr('');
    try {
      await api.post(`/marketplace/alerts/cancel?token=${encodeURIComponent(token)}`);
      track('unsubscribe_search_alert', {});
      setCancelled(true);
    } catch (e) { setErr(e.response?.data?.detail || 'Não foi possível cancelar.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-12" data-testid="alert-manage-page">
      <div className="mx-auto max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="inline-flex items-center gap-2 text-lg font-extrabold text-slate-900">
          <BellRing className="h-5 w-5 text-emerald-600" /> Gerir o seu alerta
        </h1>

        {loading ? (
          <p className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> A carregar…</p>
        ) : cancelled ? (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="alert-manage-cancelled">
            <Check className="mx-auto h-6 w-6 text-emerald-600" />
            <p className="mt-1 text-sm font-semibold text-emerald-800">Alerta cancelado. Não voltará a receber estes avisos.</p>
            <Link to="/" className="mt-3 inline-block text-xs font-semibold text-emerald-700 underline">Voltar ao Marketplace</Link>
          </div>
        ) : err && !data ? (
          <div className="mt-4 text-center" data-testid="alert-manage-invalid">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <XCircle className="h-6 w-6 text-amber-600" />
            </div>
            <h2 className="mt-3 text-base font-bold text-slate-900">Ligação inválida ou expirada</h2>
            <p className="mx-auto mt-1 max-w-sm text-sm text-slate-600">
              Esta ligação de gestão já não é válida. Pode criar um novo alerta ou contactar o suporte se precisar de ajuda.
            </p>
            <div className="mt-4 flex flex-col justify-center gap-2 sm:flex-row">
              <Link to="/viaturas" className="inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-600" data-testid="alert-invalid-new">
                <BellRing className="h-4 w-4" /> Criar novo alerta
              </Link>
              <Link to="/" className="inline-flex items-center justify-center rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:border-slate-400" data-testid="alert-invalid-home">
                Voltar ao Marketplace
              </Link>
            </div>
          </div>
        ) : data && (
          <>
            <div className="mt-4 rounded-xl bg-slate-50 p-3 text-sm text-slate-600" data-testid="alert-manage-summary">
              <p><strong>{data.email_mascarado}</strong> · estado: <span className="font-semibold">{data.estado}</span></p>
              <p className="mt-1 text-xs">Critérios: {[data.criterios?.distrito, data.criterios?.finalidade, data.criterios?.tipo].filter(Boolean).join(' · ') || 'qualquer viatura'}</p>
            </div>

            {data.consentimento_retirado && (
              <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700">Consentimento retirado — reativar exige nova subscrição.</p>
            )}

            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-500">Frequência</p>
              <div className="mt-1 flex flex-wrap gap-2">
                {Object.entries(FREQ_LABEL).map(([k, label]) => (
                  <button key={k} type="button" disabled={busy || data.estado === 'cancelado'} onClick={() => changeFreq(k)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${data.frequencia === k ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-300 text-slate-600'}`}
                    data-testid={`alert-freq-${k}`}>{label}</button>
                ))}
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {data.estado === 'confirmado' && (
                <button type="button" onClick={pause} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600" data-testid="alert-pause">
                  <Pause className="h-3.5 w-3.5" /> Pausar
                </button>
              )}
              {data.estado === 'pausado' && (
                <button type="button" onClick={reactivate} disabled={busy || data.consentimento_retirado} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500 px-3 py-2 text-xs font-semibold text-emerald-700" data-testid="alert-reactivate">
                  <Play className="h-3.5 w-3.5" /> Reativar
                </button>
              )}
              {data.estado !== 'cancelado' && !confirmCancel && (
                <button type="button" onClick={() => setConfirmCancel(true)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-2 text-xs font-semibold text-red-600" data-testid="alert-cancel">
                  <XCircle className="h-3.5 w-3.5" /> Cancelar alerta
                </button>
              )}
            </div>

            {confirmCancel && data.estado !== 'cancelado' && (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3" data-testid="alert-cancel-confirm">
                <p className="text-xs font-semibold text-red-700">Tem a certeza? Deixará de receber estes avisos e o seu email entra na lista de não contacto.</p>
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={doCancel} disabled={busy} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white" data-testid="alert-cancel-yes">
                    {busy ? 'A cancelar…' : 'Sim, cancelar'}
                  </button>
                  <button type="button" onClick={() => setConfirmCancel(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600">Manter</button>
                </div>
              </div>
            )}

            {err && <p className="mt-3 text-xs font-semibold text-red-600">{err}</p>}
          </>
        )}
      </div>
    </div>
  );
}
